// AboutPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from '../../components/PageHeader';
import { api } from '../../api/client';
import type { Contact } from '../../api/types';
import {
    DEFAULT_BIO_LEFT,
    DEFAULT_BIO_RIGHT,
    DEFAULT_BIO_PHOTO,
    DEFAULT_CONTACTS,
    DefaultContact,
} from '../../data/bio';
import { renderContactIcon } from '../../data/contactIcons';
import './About.css';

type DisplayContact = Pick<Contact, 'label' | 'value' | 'type' | 'icon'>;

function AboutPage() {
    const [bioLeft, setBioLeft] = useState(DEFAULT_BIO_LEFT);
    const [bioRight, setBioRight] = useState(DEFAULT_BIO_RIGHT);
    const [bioPhoto, setBioPhoto] = useState(DEFAULT_BIO_PHOTO);
    const [contacts, setContacts] = useState<DisplayContact[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        Promise.allSettled([
            api.content.get('bio_left'),
            api.content.get('bio_right'),
            api.content.get('bio_photo'),
            api.contacts.list(),
        ]).then(([l, r, p, c]) => {
            if (cancelled) return;
            if (l.status === 'fulfilled' && l.value.value) setBioLeft(l.value.value);
            if (r.status === 'fulfilled' && r.value.value) setBioRight(r.value.value);
            if (p.status === 'fulfilled' && p.value.value) setBioPhoto(p.value.value);
            if (c.status === 'fulfilled') setContacts(c.value);
        });
        return () => { cancelled = true; };
    }, []);

    const displayContacts: DisplayContact[] = useMemo(
        () => (contacts && contacts.length > 0 ? contacts : (DEFAULT_CONTACTS as DefaultContact[])),
        [contacts],
    );
    const socials = displayContacts.filter((c) => c.type === 'social');
    const emails = displayContacts.filter((c) => c.type === 'email');

    return (
        <div className="about-page">
            <Helmet>
                <title>About Lo.Sai - Lorenzo Saini | Sound Artist & Producer</title>
                <meta name="description" content="Lo.Sai is the moniker of Lorenzo Saini, composer, sound artist, and electronic music producer. Creator of Maitake EP on Danza Tribale. Classically trained, working at the intersection of scoring, sound art, performance, and electronic music." />
                <link rel="canonical" href="https://lorenzosaini.com/about" />
                <meta property="og:title" content="About Lo.Sai - Lorenzo Saini" />
                <meta property="og:description" content="Lo.Sai is the moniker of Lorenzo Saini, composer, sound artist, and electronic music producer. Creator of Maitake EP. Classically trained, working at the intersection of scoring, sound art, performance, and electronic music." />
                <meta property="og:url" content="https://lorenzosaini.com/about" />
                <meta name="twitter:title" content="About Lo.Sai - Lorenzo Saini" />
                <meta name="twitter:description" content="Lo.Sai is the moniker of Lorenzo Saini, composer, sound artist, and electronic music producer. Creator of Maitake EP on Danza Tribale." />
            </Helmet>
            <PageHeader title="ABOUT" />

            <div className="content-container">
                {/* ======= 1. Intro Text (Two-Column) ======= */}
                <section className="intro-section">
                    <div className="bio-header">
                        <h1 className="bio-title">
                            <span className="name-desktop">Lo.Sai | Lorenzo Saini</span>
                            <span className="name-mobile">Lo.Sai | Lorenzo Saini</span>
                        </h1>
                        <h2 className="bio-subtitle">Composer, Sound Artist and Electronic Music Producer</h2>
                    </div>
                    <div className="bio-content">
                        <div className="intro-left">
                            <p>{bioLeft}</p>
                        </div>
                        <div className="intro-right">
                            <p>{bioRight}</p>
                        </div>
                    </div>

                </section>

                {/* ======= 2. Contact Information & Find My Music ======= */}
                <section className="contact-section">


                    {socials.length > 0 && (
                        <div className="music-section">
                            <h3 className="find-music-title">Find My Music On</h3>
                            <ul className="music-links">
                                {socials.map((s, i) => (
                                    <li key={`${s.icon}-${s.value}-${i}`}>
                                        <a
                                            href={s.value}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={s.label || s.icon}
                                            title={s.label || undefined}
                                        >
                                            {renderContactIcon(s.icon, 'music-icon')}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {emails.length > 0 && (
                        <div className="contact-info">
                            <h3>Contact</h3>
                            {emails.map((e, i) => (
                                <div className="contact-item" key={`${e.value}-${i}`}>
                                    {renderContactIcon(e.icon || 'envelope', 'contact-icon')}
                                    <div>
                                        {e.label && <strong>{e.label}: </strong>}
                                        <a href={`mailto:${e.value}`}>{e.value}</a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* ======= 3. Profile Photo ======= */}
                <section className="gallery-section">
                    <div className="gallery-single">
                        <div className="gallery-item">
                            <img src={bioPhoto} alt="Lorenzo Saini" />
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default AboutPage;